/* ==========================================================================
   SPLASH Hourly Draw — the data module, wired to the chain layer.

   This replaces the candidate build's hand-written rehearsal file. It publishes
   exactly the same five-method `window.SplashHourly` object, so hourly.js
   (ported unchanged to hourly-view.js) is untouched: every pixel of the design
   is the designer's, and every number in it now comes from the same reads
   weekly-data.js uses, pointed at the speed realm instead of the raffle one.

   THE RULE THIS FILE IS BUILT TO: read the constants, never restate them. The
   three errors corrected on 2026-10-07 — a 24-block forfeit window that is
   really 26,182, an 1,800-block hour that is really 1,090, a 2-second block
   that is really 3.3 — were one mistake repeated: a number the realm already
   publishes, written down a second time by hand. So there are no chain
   constants in this file. Ticket price and the window's length come from
   `economics('speed')`, which reads the realm's own exported consts (or, for
   the window length, its own WindowBlocks() read — see draws.js). The one
   exception is named where it occurs.

   WHAT "LIVE" MEANS HERE. The speed realm IS deployed on onyx now -- it went up
   on 2026-10-07; game/ONYX-DEPLOY-2026-10-07.md has the heights and what was
   verified. This paragraph said "not deployed yet" for hours after that stopped
   being true, which is the exact shape of staleness the rule above is written
   against: a statement correct when written and now simply false. `makeClient` and `makeDrawMock` share one
   interface precisely so this file does not have to care: when the realm is up
   the reads are real, and until then they come from the rehearsal client while
   the page still reports the real chain height beside them. The banner says
   which, and it must never say "live" when it is not.
   ========================================================================== */
import './polyfill.js';
import { CONFIG } from './config.js';
import * as live from './chain/reads.js';
import { makeClient, economics } from './chain/draws.js';
import { makeDrawMock } from './chain/drawmock.js';
import { readLive, readRecent, drawState, blockSecondsFor } from './draw.js';
import * as wallet from './chain/wallet.js';
import { realmAddress } from './chain/addr.js';

const REALM = CONFIG.speedRealm;
const RECENT = 3;

// ugnot -> GNOT, and ONLY at the boundary. Every figure above this line is an
// exact BigInt of ugnot; the view wants plain numbers, so the lossy step
// happens once, here, where it can be seen.
const gnot = (u) => Number(u) / 1e6;

const S = {
  mode: 'rehearsal',
  client: null,
  econ: null,
  chain: null,
  liveStatus: null,
  deployed: false,
  live: null,
  recent: null,
  closeBlock: null,
  beaconRound: null,
  payoutAt: null,    // unix seconds the pinned drand round lands; see compose()
  payoutRound: null, // which round payoutAt was read for, so it is read once
  me: null,
  master: null,
  cadenceMs: 4000,
  lastTotal: null,
  ownBuyPending: false,
  err: {},
};

const listeners = [];
let state = null;

// The view's contract, copied from the candidate's own module rather than
// guessed: `{type:'state', state}` renders, `{type:'entry', count, you}` drops
// a splash on the lagoon, `{type:'error', message}` shows the retry panel.
// Anything else is silently ignored by it, which is how the weekly job's first
// draft sat on "Reading the realm…" with no error.
function emit(evt) {
  for (const fn of listeners) {
    try { fn(evt); } catch (e) { console.error('SplashHourly listener threw', e); }
  }
}
const publish = () => emit({ type: 'state', state });
const fail = (message) => emit({ type: 'error', message });

/* --- the shape the view asks for, assembled from the reads ---------------- */
function compose() {
  const L = S.live;
  const econ = S.econ;

  // A failed read is a failed read. The design has an offline state; it is for
  // exactly this, and it is better than a stale number with no mark on it.
  if (!L || !econ) {
    state = { offline: true, error: (S.err.live ?? S.err.chain)?.message ?? 'the realm could not be read' };
    return state;
  }

  const phase = drawState(
    { committed: L.committed, resolved: false, abandoned: false, round: L.round },
    { index: L.index, current: L.index, beaconRound: null, liveTickets: L.entries },
  ) === 'selling' ? 'open' : L.committed ? 'drawing' : 'drawn';

  const rows = S.recent?.rows ?? [];
  const past = rows.filter((r) => r.index < L.index && r.resolved);

  state = {
    rehearsal: S.mode === 'rehearsal',
    network: CONFIG.chainId,
    // The REAL chain's height, even in rehearsal — it is what makes a rehearsal
    // visibly a rehearsal rather than a mock pretending to be a chain.
    networkHeight: S.liveStatus ? Number(S.liveStatus.height) : null,
    window: Number(L.index),
    phase,
    currentBlock: S.chain ? Number(S.chain.height) : null,
    closeBlock: S.closeBlock == null ? null : Number(S.closeBlock),
    // speed.gno's WindowBlocks(), read through economics() rather than
    // restated — see draws.js economics(): "WindowBlocks() — read live when it
    // is up", with CONFIG.draw.speedWindowBlocks only as the fallback for a
    // chain where the realm is not deployed.
    windowBlocks: Number(econ.periodBlocks),
    // THE REHEARSAL'S BLOCKS REALLY DO ARRIVE AT ITS OWN CADENCE, so quoting
    // onyx's measured rate here would put the countdown out by the ratio
    // between them. drawmock exposes blockSeconds() for exactly this — it
    // knows it advances two blocks every cadenceMs — so that is read rather
    // than recomputed from cadenceMs by hand. blockSecondsFor (the live path)
    // returns {seconds, source, chainId}; taking the object whole is how the
    // weekly page first printed "[object Object] seconds a block".
    secondsPerBlock: S.mode === 'rehearsal'
      ? S.client.blockSeconds()
      : blockSecondsFor(S.chain?.chainId ?? CONFIG.chainId).seconds,
    // The round pinned for THIS window, not the beacon's current head. The
    // distinction is the whole fairness claim: it was fixed before the
    // window's first entry existed.
    randomnessRound: L.round > 0n ? Number(L.round) : null,
    // WHEN THE MONEY ACTUALLY ARRIVES, which is NOT when the clock hits zero
    // and nothing on this page said so. The window closes, and the drand
    // round it is pinned to lands about two hours LATER -- that gap is the
    // security margin, deliberately. A player watching an "hourly" draw tick
    // to 0:00 and then seeing nothing happen for two hours has every reason
    // to think it is broken. Unix seconds, or null when the window has no
    // entries yet and therefore no pin.
    payoutAt: S.payoutAt,
    // speed.gno's pot is entry money only — DrawOf says there is no sponsored
    // element, so fromBuyers (pot minus sponsored) equals the pot exactly. It
    // is used anyway, for the same reason as the weekly page: what a player
    // reads as "the pot" and what they could reclaim on a refund are the
    // same read, never two.
    pot: gnot(L.fromBuyers),
    // The connected address, so the header can stop saying "Connect wallet"
    // after one is connected. null when nobody is.
    wallet: S.me ? { address: S.me } : null,
    entries: { total: Number(L.entries), mine: S.me ? Number(L.mine) : null },
    entry: {
      price: gnot(econ.ticketPrice),
      // The one constant not read from the realm: houseCutBps is unexported.
      // CONFIG carries it with a comment saying it must track the contracts,
      // and both raffle_test.gno and speed's own test pin the same value.
      houseShare: Number(CONFIG.draw.houseCutBps) / 10000,
    },
    // NOT WIRED: the live window's own record is never resolved by
    // construction — readLive asks for the CURRENT index, and a window only
    // resolves after it stops being current, by which point this read has
    // already moved on to the next one. A winner for the window just gone
    // shows up in `history` instead; there is no read that puts one here.
    winner: null,
    // A DRAW THAT IS OVER AND NOT SETTLED. This is the one failure a player can
    // see and we cannot hide: the window closed, the money is still in the
    // contract, and nobody has pressed the buttons that finish it.
    //
    // IT IS DETECTED FROM THE CHAIN, NOT FROM OUR KEEPER. The page has no idea
    // whether our process is alive, out of gas, or was never started, and it
    // should not -- "the draw has not settled" is true and actionable in every
    // one of those cases, and it stays true if we disappear entirely. A banner
    // reading "our server is low on funds" would be both worse information and
    // the wrong abstraction.
    // THE ROUND AND WHETHER IT IS DUE ARE PART OF THIS, and leaving them out
    // was the defect. The banner used to say "anyone can finish it" in every
    // stalled state -- including the one where NOBODY can, because the drand
    // round the window is pinned to has not happened yet. That reads as "the
    // operators have abandoned your money" when the truth is "the dice have
    // not been rolled yet, come back shortly". Two genuinely different
    // situations needing two different sentences, so the data layer has to
    // tell them apart.
    stalled: (() => {
      const s = rows.find((r) => r.index < L.index && !r.resolved);
      if (!s) return null;
      const round = Number(s.round);
      // null, not false, when the beacon round is unknown -- in rehearsal
      // mode or if that read failed. The view then says neither "waiting" nor
      // "ready", because asserting either would be a guess.
      const due = S.beaconRound == null || !round ? null : round <= S.beaconRound;
      return { index: Number(s.index), pot: gnot(s.pot), round, due };
    })(),
    history: past.map((r) => ({
      window: Number(r.index),
      winner: r.winner,
      amount: gnot(r.pot),
      entries: Number(r.frozenTotal),
      round: Number(r.round),
    })),
    // A PATH, not a URL. The design prints this as text; handing it a full
    // https:// URL made the weekly page's proof panel render a wrapped gnoweb
    // address where a package path belongs.
    realm: REALM,
  };
  return state;
}

/* --- the poll ------------------------------------------------------------- */
async function refresh() {
  const c = S.client;
  const jobs = await Promise.allSettled([
    c.chainStatus(),
    readLive(c, S.me, 'speed'),
    readRecent(c, RECENT, 'speed'),
    c.currentEndHeight(),
    // The beacon's own current round, so the unsettled banner can tell
    // "waiting for the dice" from "ready, somebody press it". Live only --
    // the mock client has no beacon -- and failure is not fatal: the banner
    // falls back to saying neither.
    S.mode === 'live' ? live.currentRound() : Promise.resolve(null),
  ]);
  const [st, lv, rc, eh, br] = jobs;

  S.err = {};
  if (st.status === 'fulfilled') S.chain = st.value; else { S.chain = null; S.err.chain = st.reason; }
  if (lv.status === 'fulfilled') S.live = lv.value; else { S.live = null; S.err.live = lv.reason; }
  if (rc.status === 'fulfilled') S.recent = rc.value; else { S.recent = null; S.err.recent = rc.reason; }
  if (eh.status === 'fulfilled') S.closeBlock = BigInt(eh.value); else { S.closeBlock = null; S.err.close = eh.reason; }
  S.beaconRound = br.status === 'fulfilled' && br.value != null ? Number(br.value) : null;

  // The pinned round's wall-clock time, read from the BEACON rather than
  // computed here. drand's genesis and period are the beacon's constants and
  // duplicating them in JavaScript is how the two drift apart; the realm is
  // the authority on its own clock. Only re-read when the round changes --
  // it is fixed for the life of a window, so polling it every four seconds
  // would be three extra chain reads a minute for a number that cannot move.
  const pinned = S.live && S.live.round > 0n ? Number(S.live.round) : null;
  if (pinned == null) {
    S.payoutAt = null;
    S.payoutRound = null;
  } else if (pinned !== S.payoutRound) {
    try {
      S.payoutAt = Number(await live.timeOfRound(pinned));
      S.payoutRound = pinned;
    } catch (e) {
      S.payoutAt = null;   // unknown, and the view says so rather than guessing
    }
  }

  compose();
  if (state.offline) { fail(state.error); return; }

  // A splash on the lagoon when entries grow, within the same window. Caught
  // here rather than invented: the rehearsal has no scripted "someone else
  // buys" queue (that was the standalone candidate's own fakery), so in
  // rehearsal mode this fires only for an actually-completed buy — ours, or
  // another tab's against the same mock. ownBuyPending tells the two apart so
  // the splash is not misattributed to a stranger.
  if (S.lastTotal != null && state.window === S.lastWindow && state.entries.total > S.lastTotal) {
    emit({ type: 'entry', count: state.entries.total - S.lastTotal, you: S.ownBuyPending });
  }
  S.lastTotal = state.entries.total;
  S.lastWindow = state.window;
  S.ownBuyPending = false;

  publish();
}

/* --- boot ----------------------------------------------------------------- */
async function start() {
  const q = new URLSearchParams(location.search);
  const forceDemo = q.has('demo') || location.hash === '#rehearsal';
  S.cadenceMs = Number(q.get('cadence') || 4000);

  try {
    S.liveStatus = await live.chainStatus();
    S.deployed = forceDemo ? false : await live.isDeployed(REALM);
  } catch (e) {
    console.warn('chain unreachable', e);
    S.err.chain = e;
  }

  if (S.deployed) {
    S.mode = 'live';
    S.client = makeClient('speed');
    S.client.chainStatus = live.chainStatus;
    S.econ = await economics('speed');
  } else {
    S.mode = 'rehearsal';
    S.client = makeDrawMock({ game: 'speed', cadenceMs: S.cadenceMs });
    S.me = S.client.me;
    S.econ = S.client.economics();
  }


  // Carry a wallet across pages. Each of the three screens is a separate
  // document, so without this the arcade forgets you the moment you click
  // another game. Silent by design -- see wallet.resumeAdena.
  if (S.mode === 'live') {
    const back = (await wallet.resumeMnemonic().catch(() => null))
      ?? (await wallet.resumeAdena().catch(() => null));
    if (back) { S.me = back.address; S.master = back; }
  }

  await refresh();
  setInterval(refresh, S.mode === 'rehearsal' ? 1500 : CONFIG.pollMs);
}

/* --- the two write paths, deliberately not yet wired ---------------------- */
//
// Reads first, on purpose, the same choice the weekly screen made: prove the
// mapping without touching a payment path. Getting in goes through the same
// allowance-then-call dance the old speed page already implements, and that is
// worth porting carefully rather than quickly.
// WIRED 2026-10-07, right after the weekly one -- and the gap between the two is
// the lesson. I wired buying on the weekly screen, said "buying is wired", and
// the owner went to the HOURLY screen and hit the refusal that was still there.
// Two screens that do the same job are two screens, and "done" means both.
//
// Same flow as weekly-data.js's buyTickets, against the speed realm: approve the
// allowance, then call. See that file for why the order matters, why wrapping is
// not done on the player's behalf, and why none of the signing branches are
// covered by any test.
async function buyEntries(count) {
  const n = BigInt(Math.max(1, Math.floor(Number(count) || 1)));

  if (S.mode === 'rehearsal') {
    if (!S.client?.buy) throw new Error('The rehearsal cannot sell tickets.');
    S.ownBuyPending = true;
    await S.client.buy(n);
    await refresh();
    return { entries: Number(n) };
  }

  if (!S.master || !S.me) throw new Error('Connect a wallet first.');
  if (!S.econ) throw new Error('The realm has not been read yet — try again in a moment.');

  const cost = n * BigInt(S.econ.ticketPrice);
  const spender = realmAddress(REALM);

  // Work out what this purchase needs BEFORE asking for a signature, so the
  // player sees one dialog describing the whole thing rather than three.
  let wrap = 0n;
  let approve = null;
  const allowance = BigInt(await live.wugnotAllowance(S.me, spender));
  if (allowance < cost) {
    approve = cost;
    const held = BigInt(await live.wugnotBalance(S.me));
    if (held < cost) {
      wrap = cost - held;
      const native = BigInt(await live.ugnotBalance(S.me));
      const feeFloor = 2_000_000n;
      if (native < wrap + feeFloor) {
        throw new Error(
          'You need ' + gnot(wrap) + ' more GNOT wrapped and hold ' + gnot(native) + ' unwrapped. '
          + 'Top up from the faucet at https://faucet.gno.land first.',
        );
      }
    }
  }
  await sendBatch(msgsFor(REALM, 'Buy', [String(n)], { wrap, approve, spender }));
  await refresh();
  return { entries: Number(n) };
}



// Build the whole purchase as ONE transaction: wrap if needed, approve if
// needed, then buy. Returns the message list in order.
//
// ONE SIGNATURE INSTEAD OF THREE, which is what the owner asked for -- and it is
// not only fewer prompts. The messages are atomic: a failure anywhere reverts
// the lot, so there is no longer a state where the player wrapped and approved
// and then the purchase failed, leaving money converted for a thing they did not
// get. The three-prompt version could do exactly that.
function msgsFor(pkgPath, func, args, { wrap = 0n, approve = null, spender = null } = {}) {
  const m = S.master;
  if (!m) throw new Error('Connect a wallet first.');
  const json = m.kind === 'adena';
  const out = [];
  if (wrap > 0n) {
    out.push(json
      ? wallet.callJsonSend(S.me, CONFIG.wugnotRealm, 'Deposit', [], String(wrap) + 'ugnot')
      : wallet.callProtoSend(S.me, CONFIG.wugnotRealm, 'Deposit', [], String(wrap) + 'ugnot'));
  }
  if (approve != null) {
    out.push(json
      ? wallet.callJson(S.me, CONFIG.wugnotRealm, 'Approve', [spender, String(approve)])
      : wallet.callProto(S.me, CONFIG.wugnotRealm, 'Approve', [spender, String(approve)]));
  }
  out.push(json
    ? wallet.callJson(S.me, pkgPath, func, args)
    : wallet.callProto(S.me, pkgPath, func, args));
  return out;
}

function sendBatch(msgs) {
  const m = S.master;
  if (!m) throw new Error('Connect a wallet first.');
  if (m.kind === 'key') return wallet.keySend(m, msgs);
  if (m.kind === 'adena') return wallet.adenaSend(msgs);
  throw new Error('That wallet cannot sign from this page.');
}



// WIRED 2026-10-07, read-only. See weekly-data.js's copy for the full argument;
// the short version is that connecting obtains an ADDRESS and nothing else, so
// the page can show your entries rather than only the total. Nothing is signed
// and no money moves. The buy path above stays refused on purpose.
async function connectWallet() {
  if (S.mode === 'rehearsal') {
    S.me = S.client?.me ?? null;
    await refresh();
    return { address: S.me, kind: 'rehearsal' };
  }
  wallet.clearDisconnected(); // an explicit connect overrides an earlier disconnect
  const resumed = await wallet.resumeMnemonic().catch(() => null);
  const w = resumed ?? (wallet.hasAdena()
    ? await wallet.connectAdena()
    : (() => { throw new Error('No wallet found. Install Adena and point it at ' + CONFIG.chainId + '.'); })());
  S.me = w.address;
  S.master = w;
  await refresh();
  // The view toasts res.message on success. Returning only {address, kind}
  // made it toast the word "undefined" -- found by reading the view's handler
  // rather than by seeing it, because the happy path needs a real extension.
  return {
    address: w.address,
    kind: w.kind,
    message: 'Connected ' + w.address.slice(0, 8) + '…' + w.address.slice(-4) + '. Your entries will show here.',
  };
}


// Disconnect. Forgets the wallet HERE and nothing else: a page cannot un-approve
// itself in Adena, and pretending otherwise would be a lie about what happened.
// The flag is what makes it stick across the other two screens.
async function disconnect() {
  wallet.forgetMnemonic();
  wallet.markDisconnected();
  S.me = null;
  S.master = null;
  await refresh();
  return { message: 'Disconnected here. Adena still has this site approved \u2014 remove it in the extension to undo that.' };
}

window.SplashHourly = {
  config: { mode: S.mode, network: CONFIG.chainId, realm: REALM },
  start,
  onChange(fn) { listeners.push(fn); if (state && !state.offline) fn({ type: 'state', state }); },
  buyEntries,
  connectWallet,
  disconnect,
};
